import * as React from 'react';
import * as moment from 'moment';
import { IWatrRequest, IWatrRequestAuditLog } from '@nkr/models/index';
import { WageAndTravelCategory, WageAndTravelReimbursementRequestStatus, WageAndTravelStatus } from '@nkr/enums/index';
import styled from 'styled-components';
import { Glyphicon, Overlay, OverlayTrigger, Popover } from 'react-bootstrap';
import { WATR_LOG_ACTION_TYPE } from './audit-log-action-groups';
import { isHourly, isSelfEmployed } from './utils/employment-type.utils';
import { getVerifyTaxStepLabel, requiresTaxDocument } from './utils/verify-tax-step-label';
import { requestOpenAuditLogDeclined } from './watr-audit-log-focus';

const REQUEST_FILE_TYPE_SSN = 8;

export interface IStepItem {
    key: string;
    label: string;
    status: 'completed' | 'current' | 'upcoming';
    stepNumber: number;
    summary?: string;
}

export interface IPriorDeclineEntry {
    logDate: string;
    dateDisplay: string;
    fixableStatus: 'Fixable' | 'Permanent' | null;
    dimensions: string[];
    reasonSummary: string | null;
    logMessage: string;
    updatedByAlias: string;
}

export interface IPriorDeclineInfo {
    count: number;
    entries: IPriorDeclineEntry[];
    last: IPriorDeclineEntry | null;
    badgeLabel: string;
}

export interface IVerificationStepInfo {
    requestTypeLabel: string;
    steps: IStepItem[];
    currentStepIndex: number;
    totalSteps: number;
    currentStepKey: string;
    prerequisiteHint: string | null;
    postVerificationNote: string | null;
    donationDateDisplay: string | null;
    travelAfterWageNote: boolean;
    priorDecline: IPriorDeclineInfo | null;
    pivotal: {
        donationConfirmed: boolean;
        taxDocReceived: boolean;
        ssnDocReceived: boolean;
        payStubReceived: boolean;
        selfEmployedRequired: boolean;
        selfEmployedReceived: boolean;
        travelPendingCount: number;
    };
}

const WAGE_STEP_KEYS = ['pending_documents', 'verify_tax', 'verify_stub', 'verify_amount', 'post_verification'];
const WEEK_DECLINE_RE = /^Week \d+ declined/i;
const REQUEST_DECLINED_RE = /^Request declined\b/i;
const FIXABLE_STATUS_RE = /\((Fixable|Permanent)\)/i;
const DIMS_RE = /\[([^\]]*)\]/;

function isValidPayStubDate(value: string | null | undefined): boolean {
    if (!value) return false;
    const y = moment(value).year();
    return !isNaN(y) && y > 1;
}

function isRequestLevelDeclineLog(log: IWatrRequestAuditLog): boolean {
    const msg = (log.logMessage || '').trim();
    if (!msg) return false;
    if (WEEK_DECLINE_RE.test(msg)) return false;

    const actionType = log.logActionType != null && log.logActionType > 0 ? log.logActionType : null;
    if (actionType === WATR_LOG_ACTION_TYPE.Denial) {
        return REQUEST_DECLINED_RE.test(msg) || msg.toLowerCase().includes('request declined');
    }

    return REQUEST_DECLINED_RE.test(msg);
}

function parsePriorDeclineEntry(log: IWatrRequestAuditLog): IPriorDeclineEntry {
    const msg = (log.logMessage || '').trim();
    const fixableMatch = msg.match(FIXABLE_STATUS_RE);
    const fixableStatus = fixableMatch
        ? (fixableMatch[1] as 'Fixable' | 'Permanent')
        : null;

    const dimsMatch = msg.match(DIMS_RE);
    let dimensions: string[] = [];
    if (dimsMatch) {
        const raw = dimsMatch[1].trim();
        if (raw.length > 0 && raw.toLowerCase() !== 'none') {
            dimensions = raw.split(',').map(s => s.trim()).filter(Boolean);
        }
    }

    let reasonSummary: string | null = null;
    const reasonMatch = msg.match(/\]:\s*([\s\S]+)$/) || msg.match(/Request declined[^:]*:\s*([\s\S]+)$/i);
    if (reasonMatch && reasonMatch[1]) {
        const after = reasonMatch[1].trim()
            .replace(/<br\s*\/?>/gi, ' ')
            .replace(/<[^>]+>/g, '')
            .replace(/&nbsp;/gi, ' ')
            .replace(/\s+/g, ' ')
            .trim();
        if (after.length > 0) {
            reasonSummary = after;
        }
    }

    const parsed = moment(log.logDate);
    const dateDisplay = parsed.isValid() ? parsed.format('MMM D, YYYY · h:mm A') : (log.logDate || '');

    return {
        logDate: log.logDate,
        dateDisplay,
        fixableStatus,
        dimensions,
        reasonSummary,
        logMessage: msg,
        updatedByAlias: log.updatedByAlias || ''
    };
}

export function getPriorDeclineInfo(data: IWatrRequest | null): IPriorDeclineInfo | null {
    if (!data || !data.logs || data.logs.length === 0) return null;

    const entries = data.logs
        .filter(isRequestLevelDeclineLog)
        .map(parsePriorDeclineEntry)
        .sort((a, b) => {
            const am = moment(a.logDate);
            const bm = moment(b.logDate);
            if (am.isValid() && bm.isValid()) return bm.valueOf() - am.valueOf();
            return 0;
        });

    if (entries.length === 0) return null;

    const last = entries[0];
    const count = entries.length;
    const badgeLabel = count === 1
        ? 'Previously declined (1×)'
        : `Previously declined (${count}×)`;

    return {
        count,
        entries,
        last,
        badgeLabel
    };
}

export function getVerificationStepInfo(
    data: IWatrRequest | null,
    category?: WageAndTravelCategory
): IVerificationStepInfo | null {
    if (!data) return null;

    const status = data.status;
    const requestTravels = data.requestTravels || [];
    const travelPendingCount = requestTravels.filter(
        x => x.status === WageAndTravelReimbursementRequestStatus.PendingApproval
    ).length;
    const priorDecline = getPriorDeclineInfo(data);

    const isVerifyTravel =
        travelPendingCount > 0 &&
        (
            (data.donationConfirmed
                && (category === WageAndTravelCategory.PendingDERVerify || status === WageAndTravelStatus.PendingNkrVerifyTravel))
            || status === WageAndTravelStatus.PendingAdvancedReimbursement
        );

    const isWageRelated =
        status === WageAndTravelStatus.PendingDocuments ||
        (status >= WageAndTravelStatus.PendingNkrVerifyTax && status <= WageAndTravelStatus.PendingNkrVerifyAmount) ||
        (status >= WageAndTravelStatus.PendingSurgery && status < WageAndTravelStatus.PendingTravelReceipts);

    const pivotal = {
        donationConfirmed: !!data.donationConfirmed,
        taxDocReceived: !!data.taxDocumentFile,
        ssnDocReceived: (data.fileUploadRecords || []).some((r: any) => {
            const type = Number(r.fileType);
            return r.isActive !== false && !Number.isNaN(type) && type === REQUEST_FILE_TYPE_SSN;
        }),
        payStubReceived: !!data.payStubFile,
        selfEmployedRequired: !!data.requireSelfEmployedAffirmation,
        selfEmployedReceived: !!data.selfEmployedAffirmationFile,
        travelPendingCount,
    };

    const taxDocRequired = requiresTaxDocument(data);
    const selfEmployed = isSelfEmployed(data.employmentType);

    const hasWage = isWageRelated || (status >= WageAndTravelStatus.PendingNkrVerifyTax && status <= WageAndTravelStatus.PendingNkrVerifyAmount) || (status >= WageAndTravelStatus.PendingSurgery && status < WageAndTravelStatus.PendingTravelReceipts);
    const hasTravel = (requestTravels && requestTravels.length > 0) || status >= WageAndTravelStatus.PendingTravelReceipts;
    let requestTypeLabel = 'Request';
    if (hasWage && hasTravel) requestTypeLabel = 'Lost wage & Travel expenses';
    else if (hasTravel) requestTypeLabel = 'Travel expenses only';
    else if (hasWage) requestTypeLabel = 'Lost wage only';

    const donationDateDisplay = data.donationDate
        ? `Donation date: ${moment(data.donationDate).format('MMM D, YYYY')}`
        : null;

    if (isVerifyTravel) {
        const postLabel = status >= WageAndTravelStatus.PendingTravelReimbursement ? 'Travel Reimbursed' : 'Complete';
        const steps: IStepItem[] = [
            { key: 'verify_travel', label: 'Verify Travel Expenses', status: 'current', stepNumber: 1 },
            { key: 'post_verification', label: postLabel, status: 'upcoming', stepNumber: 2 },
        ];
        const prerequisiteHint = travelPendingCount > 0
            ? `${travelPendingCount} travel request${travelPendingCount !== 1 ? 's' : ''} pending approval — review and approve or decline.`
            : null;
        return { requestTypeLabel, steps, currentStepIndex: 1, totalSteps: 2, currentStepKey: 'verify_travel', prerequisiteHint, postVerificationNote: null, donationDateDisplay, travelAfterWageNote: false, priorDecline, pivotal };
    }

    if (isWageRelated) {
        let currentIndex = 0;
        if (status === WageAndTravelStatus.PendingDocuments) currentIndex = 0;
        else if (status === WageAndTravelStatus.PendingNkrVerifyTax) currentIndex = 1;
        else if (status === WageAndTravelStatus.PendingNkrVerifyStub) currentIndex = 2;
        else if (status === WageAndTravelStatus.PendingNkrVerifyAmount) currentIndex = 3;
        else currentIndex = 4;

        const labels = ['Documents Submitted', getVerifyTaxStepLabel(data), 'Verify Pay Stub', 'Verify Amount', 'Verification Complete'];
        const steps: IStepItem[] = WAGE_STEP_KEYS.map((key, i) => {
            let stepStatus: 'completed' | 'current' | 'upcoming' = 'upcoming';
            if (i < currentIndex) stepStatus = 'completed';
            else if (i === currentIndex) stepStatus = 'current';
            let summary: string | undefined;
            if (stepStatus === 'completed' && key === 'verify_tax' && data.incomeIrsYear != null) {
                const gross = data.incomeIrsYearGross != null ? `$${Number(data.incomeIrsYearGross).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '';
                summary = gross
                    ? `Year ${data.incomeIrsYear}\nGross ${gross}`
                    : `Year ${data.incomeIrsYear}`;
            }
            if (stepStatus === 'completed' && key === 'verify_stub') {
                const hasValidStart = isValidPayStubDate(data.incomePayStubStartDate);
                const hasValidEnd = isValidPayStubDate(data.incomePayStubEndDate);
                const weekly = Number(data.calculatedWeeklyPayByPayStub) || 0;
                const stubCount = Array.isArray(data.paystubs) ? data.paystubs.length : 0;
                const useWeekly = isHourly(data.employmentType) && stubCount > 1 && weekly > 0;
                const amount = useWeekly ? weekly : Number(data.incomePayStubGross) || 0;
                const hasValidGross = amount > 0;
                if (hasValidStart && hasValidEnd) {
                    const range = `${moment(data.incomePayStubStartDate).format('MMM D, YYYY')} – ${moment(data.incomePayStubEndDate).format('MMM D, YYYY')}`;
                    const gross = hasValidGross ? `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '';
                    summary = gross ? `${range}\n${gross}` : range;
                } else if (hasValidGross) {
                    summary = `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                }
            }
            if (stepStatus === 'completed' && key === 'verify_amount' && data.wageReimbursementAmount != null) {
                summary = `$${Number(data.wageReimbursementAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} approved`;
            }
            return { key, label: labels[i], status: stepStatus, stepNumber: i + 1, summary };
        });
        const currentKey = WAGE_STEP_KEYS[currentIndex];
        let prerequisiteHint: string | null = null;
        if (currentKey === 'pending_documents') {
            const missing: string[] = [];
            if (taxDocRequired && !pivotal.taxDocReceived) missing.push('tax doc');
            if (!pivotal.ssnDocReceived) missing.push('SS# verification doc');
            if (!selfEmployed && !pivotal.payStubReceived) missing.push('pay stub');
            if (pivotal.selfEmployedRequired && !pivotal.selfEmployedReceived) missing.push('self-employed affirmation');
            prerequisiteHint = missing.length > 0
                ? `Waiting on donor: ${missing.join(', ')} — then verification can begin.`
                : null;
        } else if (currentKey === 'verify_tax') {
            const missing: string[] = [];
            if (taxDocRequired && !pivotal.taxDocReceived) missing.push('tax doc');
            if (!pivotal.ssnDocReceived) missing.push('SS# verification doc');
            prerequisiteHint = missing.length > 0
                ? `Required for this step: ${missing.join(', ')} must be received.`
                : null;
        } else if (currentKey === 'verify_stub') {
            prerequisiteHint = !pivotal.payStubReceived
                ? 'Required for this step: Pay stub must be received.'
                : null;
        } else if (currentKey === 'verify_amount') {
            prerequisiteHint = null;
        }
        let postVerificationNote: string | null = null;
        if (currentKey === 'post_verification') {
            postVerificationNote = 'Next: Surgery → Wage reimbursement.';
            if (hasTravel) postVerificationNote += ' Travel verification after wages.';
        }
        const travelAfterWageNote = hasTravel && currentIndex < 4;
        return { requestTypeLabel, steps, currentStepIndex: currentIndex + 1, totalSteps: 5, currentStepKey: currentKey, prerequisiteHint, postVerificationNote, donationDateDisplay, travelAfterWageNote, priorDecline, pivotal };
    }

    const steps: IStepItem[] = [
        { key: 'other', label: data.statusDescription || 'Request', status: 'current', stepNumber: 1 },
    ];
    return { requestTypeLabel, steps, currentStepIndex: 1, totalSteps: 1, currentStepKey: 'other', prerequisiteHint: null, postVerificationNote: null, donationDateDisplay, travelAfterWageNote: false, priorDecline, pivotal };
}

const TrackerCard = styled.div`
    background: #fafbfc;
    border: 1px solid #e2e8f0;
    border-left: 3px solid #2563eb;
    border-radius: 8px;
    padding: 12px 16px 14px;
    margin-bottom: 8px;
`;

const TopRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px 20px;
    margin-bottom: 12px;
`;

const RequestTypeBadge = styled.span`
    font-size: 12px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 6px;
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
`;

const StepCount = styled.p`
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #64748b;
`;

const StepperRow = styled.ol`
    display: flex;
    align-items: flex-start;
    width: 100%;
    margin: 0;
    padding: 0;
    list-style: none;
`;

const StepBlock = styled.li`
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
`;

const MarkerRow = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
`;

const ConnectorSeg = styled.div<{ $done?: boolean; $hidden?: boolean }>`
    flex: 1 1 auto;
    height: 2px;
    background: ${p => (p.$hidden ? 'transparent' : (p.$done ? '#16a34a' : '#cbd5e1'))};
`;

const InfoIconWrap = styled.span`
    flex-shrink: 0;
    margin-top: 1px;
    color: #94a3b8;
    display: inline-flex;
    align-items: center;
    cursor: pointer;

    &:hover,
    &:focus {
        color: #64748b;
        outline: none;
    }
`;

const StepCircle = styled.div<{ $status: string }>`
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 700;
    background: ${p =>
        p.$status === 'completed' ? '#16a34a' : p.$status === 'current' ? '#2563eb' : '#e2e8f0'};
    color: ${p => (p.$status === 'completed' || p.$status === 'current' ? '#fff' : '#94a3b8')};
    box-shadow: ${p => (p.$status === 'current' ? '0 0 0 3px rgba(37, 99, 235, 0.16)' : 'none')};
`;

const LabelRow = styled.div`
    display: inline-flex;
    align-items: flex-start;
    justify-content: center;
    gap: 4px;
    margin-top: 6px;
    max-width: 100%;
    padding: 0 4px;
`;

const StepLabel = styled.span<{ $status: string }>`
    font-size: 11px;
    line-height: 1.25;
    font-weight: ${p => (p.$status === 'current' ? 600 : 500)};
    color: ${p =>
        p.$status === 'completed' ? '#64748b' : p.$status === 'current' ? '#1e293b' : '#94a3b8'};
`;

const PrerequisiteHint = styled.p`
    margin: 10px 0 0 0;
    font-size: 12px;
    color: #64748b;
    line-height: 1.4;
    padding-left: 0;
`;

const PostVerificationNote = styled.p`
    margin: 8px 0 0 0;
    font-size: 12px;
    color: #475569;
    font-weight: 500;
    line-height: 1.4;
    padding-left: 0;
`;

const DonationDateBadge = styled.span`
    font-size: 11px;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 4px;
    background: #fef3c7;
    color: #92400e;
    border: 1px solid #fde68a;
`;

const DonationConfirmedBadge = styled.span<{ $confirmed: boolean }>`
    font-size: 11px;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 4px;
    background: ${p => p.$confirmed ? '#dcfce7' : '#fef3c7'};
    color: ${p => p.$confirmed ? '#166534' : '#92400e'};
    border: 1px solid ${p => p.$confirmed ? '#bbf7d0' : '#fde68a'};
`;

const PriorDeclineBadge = styled.span`
    font-size: 11px;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 4px;
    background: #fef2f2;
    color: #991b1b;
    border: 1px solid #fecaca;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 4px;
`;

const PriorDeclinePopover = styled(Popover)`
    && {
        max-width: 360px;
    }

    .popover-content {
        overflow: visible;
        padding: 10px 12px;
    }
`;

const PriorDeclinePopoverBody = styled.div`
    width: 100%;
    max-width: 320px;
    box-sizing: border-box;
    padding: 0;
`;

const PriorDeclinePopoverTitle = styled.div`
    font-size: 13px;
    font-weight: 700;
    color: #7f1d1d;
    margin-bottom: 10px;
    padding-bottom: 8px;
    border-bottom: 1px solid #fecaca;
`;

const PriorDeclineEntryBlock = styled.div`
    & + & {
        margin-top: 10px;
        padding-top: 10px;
        border-top: 1px solid #f1f5f9;
    }
`;

const PriorDeclineMetaRow = styled.div`
    display: grid;
    grid-template-columns: 64px 1fr;
    gap: 4px 8px;
    font-size: 12px;
    line-height: 1.4;
`;

const PriorDeclineMetaLabel = styled.span`
    color: #64748b;
    font-weight: 600;
`;

const PriorDeclineMetaValue = styled.span`
    color: #1e293b;
    font-weight: 500;
    word-break: break-word;
`;

const PriorDeclineReason = styled.div`
    margin-top: 8px;
    padding: 8px 10px;
    box-sizing: border-box;
    width: 100%;
    background: #f8fafc;
    border-radius: 4px;
    border: 1px solid #e2e8f0;
    border-left: 3px solid #f87171;
    font-size: 12px;
    line-height: 1.45;
    color: #334155;
    word-break: break-word;
`;

const TravelNote = styled.p`
    margin: 6px 0 0 0;
    font-size: 11px;
    color: #64748b;
    line-height: 1.3;
    padding-left: 0;
`;

interface IProps {
    data: IWatrRequest | null;
    category?: WageAndTravelCategory;
}

function formatDeclineScope(dimensions: string[]): string {
    if (dimensions.length === 0) return 'General';
    return dimensions
        .map(d => {
            const key = d.toLowerCase();
            if (key === 'wages') return 'Wages';
            if (key === 'routing') return 'Routing #';
            if (key === 'account') return 'Account #';
            return d;
        })
        .join(', ');
}

const PriorDeclineAuditLink = styled.button`
    display: inline-block;
    margin-top: 10px;
    padding: 0;
    border: none;
    background: none;
    color: #3B76B5;
    font-size: 12px;
    font-weight: 600;
    text-decoration: underline;
    cursor: pointer;
    text-align: left;

    &:hover,
    &:focus {
        color: #245269;
        outline: none;
    }
`;

const PriorDeclinePopoverContent = ({
    priorDecline,
    onNavigateToAuditLog
}: {
    priorDecline: IPriorDeclineInfo;
    onNavigateToAuditLog?: () => void;
}) => {
    const entry = priorDecline.last;
    if (!entry) {
        return null;
    }

    const openAuditLog = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        requestOpenAuditLogDeclined();
        if (onNavigateToAuditLog) {
            onNavigateToAuditLog();
        }
    };

    return (
        <PriorDeclinePopoverBody>
            <PriorDeclinePopoverTitle>
                {priorDecline.count === 1
                    ? 'Most recent decline'
                    : `Most recent of ${priorDecline.count} declines`}
            </PriorDeclinePopoverTitle>
            <PriorDeclineEntryBlock>
                <PriorDeclineMetaRow>
                    <PriorDeclineMetaLabel>Date</PriorDeclineMetaLabel>
                    <PriorDeclineMetaValue>{entry.dateDisplay || '—'}</PriorDeclineMetaValue>
                    <PriorDeclineMetaLabel>Type</PriorDeclineMetaLabel>
                    <PriorDeclineMetaValue>{entry.fixableStatus || '—'}</PriorDeclineMetaValue>
                    <PriorDeclineMetaLabel>Scope</PriorDeclineMetaLabel>
                    <PriorDeclineMetaValue>{formatDeclineScope(entry.dimensions)}</PriorDeclineMetaValue>
                    <PriorDeclineMetaLabel>By</PriorDeclineMetaLabel>
                    <PriorDeclineMetaValue>{entry.updatedByAlias || '—'}</PriorDeclineMetaValue>
                </PriorDeclineMetaRow>
                {entry.reasonSummary && (
                    <PriorDeclineReason>{entry.reasonSummary}</PriorDeclineReason>
                )}
            </PriorDeclineEntryBlock>
            {priorDecline.count > 1 && (
                <PriorDeclineAuditLink type="button" onClick={openAuditLog}>
                    View all declines in Audit Log
                </PriorDeclineAuditLink>
            )}
        </PriorDeclinePopoverBody>
    );
};

const PriorDeclineBadgeWithPopover = ({ priorDecline }: { priorDecline: IPriorDeclineInfo }) => {
    const [show, setShow] = React.useState(false);
    const badgeRef = React.useRef<HTMLSpanElement | null>(null);
    const hideTimerRef = React.useRef<number | null>(null);

    const clearHideTimer = () => {
        if (hideTimerRef.current != null) {
            window.clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }
    };

    const open = () => {
        clearHideTimer();
        setShow(true);
    };

    const scheduleClose = () => {
        clearHideTimer();
        hideTimerRef.current = window.setTimeout(() => setShow(false), 200);
    };

    React.useEffect(() => () => clearHideTimer(), []);

    return (
        <>
            <PriorDeclineBadge
                ref={badgeRef as any}
                role="button"
                tabIndex={0}
                aria-label={priorDecline.badgeLabel}
                aria-expanded={show}
                onMouseEnter={open}
                onMouseLeave={scheduleClose}
                onClick={(e) => {
                    e.preventDefault();
                    clearHideTimer();
                    setShow(v => !v);
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        clearHideTimer();
                        setShow(v => !v);
                    }
                }}
            >
                <Glyphicon glyph="warning-sign" style={{ fontSize: 11 }} />
                {priorDecline.badgeLabel}
            </PriorDeclineBadge>
            <Overlay
                show={show}
                target={() => badgeRef.current}
                placement="bottom"
                rootClose
                onHide={() => {
                    clearHideTimer();
                    setShow(false);
                }}
                container={typeof document !== 'undefined' ? () => document.body : undefined}
            >
                <PriorDeclinePopover
                    id="prior-decline-summary"
                    onMouseEnter={open}
                    onMouseLeave={scheduleClose}
                >
                    <PriorDeclinePopoverContent
                        priorDecline={priorDecline}
                        onNavigateToAuditLog={() => {
                            clearHideTimer();
                            setShow(false);
                        }}
                    />
                </PriorDeclinePopover>
            </Overlay>
        </>
    );
};

const VerificationStepTracker = (props: IProps) => {
    const info = getVerificationStepInfo(props.data, props.category);
    if (!info || info.steps.length === 0) return null;

    return (
        <TrackerCard>
            <TopRow>
                <RequestTypeBadge>{info.requestTypeLabel}</RequestTypeBadge>
                <StepCount>
                    Step {info.currentStepIndex} of {info.totalSteps}
                </StepCount>
                {info.donationDateDisplay && (
                    <>
                        <DonationDateBadge>{info.donationDateDisplay}</DonationDateBadge>
                        <DonationConfirmedBadge $confirmed={info.pivotal.donationConfirmed}>
                            {info.pivotal.donationConfirmed ? 'Donation confirmed' : 'Donation not yet confirmed'}
                        </DonationConfirmedBadge>
                    </>
                )}
                {info.priorDecline && (
                    <PriorDeclineBadgeWithPopover priorDecline={info.priorDecline} />
                )}
            </TopRow>
            <StepperRow aria-label="Verification steps">
                {info.steps.map((step, i) => (
                    <StepBlock
                        key={step.key}
                        aria-current={step.status === 'current' ? 'step' : undefined}
                    >
                        <MarkerRow>
                            <ConnectorSeg
                                $hidden={i === 0}
                                $done={i > 0 && info.steps[i - 1].status === 'completed'}
                            />
                            <StepCircle $status={step.status}>
                                {step.status === 'completed' ? (
                                    <Glyphicon glyph="ok" style={{ fontSize: 9 }} />
                                ) : (
                                    step.stepNumber
                                )}
                            </StepCircle>
                            <ConnectorSeg
                                $hidden={i === info.steps.length - 1}
                                $done={step.status === 'completed'}
                            />
                        </MarkerRow>
                        <LabelRow>
                            <StepLabel $status={step.status}>{step.label}</StepLabel>
                            {step.summary && (
                                <OverlayTrigger
                                    trigger={['hover', 'focus']}
                                    placement="bottom"
                                    overlay={
                                        <Popover id={`step-summary-${step.key}-${i}`}>
                                            <div style={{ whiteSpace: 'pre-line' }}>{step.summary}</div>
                                        </Popover>
                                    }
                                >
                                    <InfoIconWrap role="button" tabIndex={0} aria-label={`${step.label} details`}>
                                        <Glyphicon glyph="info-sign" style={{ fontSize: 11 }} />
                                    </InfoIconWrap>
                                </OverlayTrigger>
                            )}
                        </LabelRow>
                    </StepBlock>
                ))}
            </StepperRow>
            {info.prerequisiteHint && (
                <PrerequisiteHint>{info.prerequisiteHint}</PrerequisiteHint>
            )}
            {info.postVerificationNote && (
                <PostVerificationNote>{info.postVerificationNote}</PostVerificationNote>
            )}
            {info.travelAfterWageNote && (
                <TravelNote>Travel verification and reimbursement follow after wage verification.</TravelNote>
            )}
        </TrackerCard>
    );
};

export default VerificationStepTracker;
